import { useMemo, useContext, useState, useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import { marked, MarkedOptions } from "marked";
import { AppStateContext } from "../../state/AppProvider";

import { ChatResponse, parseVideoCitation, getVideoEmbedToken, buildVideoEmbedUrl } from "../../api";
import { GeneratedFile, WebSearchSource } from "../../api/models";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import { GeneratedFileChips, GeneratedImage } from "./GeneratedFileChips";
import { ButtonTooltip } from "../ButtonTooltip";

interface Props {
    answer: ChatResponse;
    isStreaming: boolean;
    onCitationClicked: (filePath: string, citationTitle: string) => void;
    onThoughtProcessClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    onCopyAnswerClicked: () => void;
    showFollowupQuestions?: boolean;
    showThoughts?: boolean;
    lastAnswer: boolean;
    windowWidth: number;
    activeVideo: string;
    onShowVideo: (citation: string) => void;
    onCloseVideo: () => void;
    streamingReasoningText?: string;
    streamingWebSearchStatus?: string;
    streamingFileGenerationStatus?: string;
}

export const Answer = ({
    answer,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onCopyAnswerClicked,
    showThoughts,
    lastAnswer,
    windowWidth,
    activeVideo,
    onShowVideo,
    onCloseVideo,
    streamingReasoningText,
    streamingWebSearchStatus,
    streamingFileGenerationStatus,
}: Props) => {
    const appStateContext = useContext(AppStateContext);
    const thoughts = answer.choices[0].context?.metadata?.thoughts || "";
    const messageContent = answer.choices[0].message.content;
    const thoughtsRef = useRef<HTMLDivElement>(null);
    const chatStreamRef = useRef<HTMLDivElement>(null);

    // Reasoning state
    const [showReasoning, setShowReasoning] = useState(false);
    const reasoningSummary = answer.choices[0].context?.metadata?.reasoning_summary || "";
    const activeReasoning = streamingReasoningText || reasoningSummary;
    const isReasoningStreaming = !!streamingReasoningText;
    const reasoningRef = useRef<HTMLDivElement>(null);
    const [reasoningExpanded, setReasoningExpanded] = useState(true);

    // Web search state
    const webSearchSources: WebSearchSource[] =
        answer.choices[0].context?.metadata?.web_search_sources ||
        answer.choices[0].context?.web_search_sources ||
        [];
    const isWebSearching = streamingWebSearchStatus === "searching";
    const hasWebSearchSources = webSearchSources.length > 0;

    const wasStopped = !!answer.choices[0].context?.stopped;
    const droppedFiles: string[] = answer.choices[0].context?.metadata?.dropped_files ?? [];

    // Generated files (code interpreter): images render inline (right-click to
    // save), only non-image files get a download chip.
    const generatedFiles: GeneratedFile[] = answer.choices[0].context?.generated_files || [];
    const generatedImages = generatedFiles.filter(f => f.is_image);
    const generatedDownloads = generatedFiles.filter(f => !f.is_image);
    const isGeneratingFiles = streamingFileGenerationStatus === "generating";
    const isSavingFiles = streamingFileGenerationStatus === "saving";

    // The streaming path sends thoughts_html alongside web_search_sources in context.
    // The non-streaming path puts web source HTML directly into metadata.thoughts.
    // Combine both to get the effective thoughts content.
    const thoughtsHtmlFromContext = answer.choices[0].context?.thoughts_html || "";
    const effectiveThoughts = thoughts !== "None" ? thoughts : thoughtsHtmlFromContext;
    const hasThoughts = effectiveThoughts && effectiveThoughts !== "None";

    // Auto-expand when reasoning starts streaming
    useEffect(() => {
        if (isReasoningStreaming) {
            setReasoningExpanded(true);
        }
    }, [isReasoningStreaming && activeReasoning.length === streamingReasoningText?.length]);

    // Auto-collapse when reasoning stops streaming
    const wasStreaming = useRef(false);
    useEffect(() => {
        if (wasStreaming.current && !isReasoningStreaming) {
            // Streaming just ended — keep the dropdown open so the user can read it
            setShowReasoning(true);
        }
        wasStreaming.current = isReasoningStreaming;
    }, [isReasoningStreaming]);

    // Auto-scroll reasoning div during streaming
    useEffect(() => {
        if (isReasoningStreaming && reasoningExpanded && reasoningRef.current) {
            reasoningRef.current.scrollTop = reasoningRef.current.scrollHeight;
        }
    }, [streamingReasoningText]);

    const reasoningTitle = useMemo(() => {
        const text = streamingReasoningText || reasoningSummary;
        if (!text) return "";
        const firstLine = text.split("\n").find((l: string) => {
            const trimmed = l.trim();
            return trimmed !== "" && !trimmed.startsWith("[WEB_SEARCH_") && !trimmed.startsWith("[FILE_GEN_");
        });
        if (!firstLine) return "";
        return firstLine.replace(/\*\*/g, "").trim();
    }, [streamingReasoningText, reasoningSummary]);

    const sanitizedReasoning = useMemo(() => {
        if (!activeReasoning) return "";
        let text = activeReasoning;
        if (reasoningTitle) {
            const lines = text.split("\n");
            const firstNonEmptyIndex = lines.findIndex((l: string) => l.trim() !== "");
            if (firstNonEmptyIndex !== -1) {
                const firstLine = lines[firstNonEmptyIndex].replace(/\*\*/g, "").trim();
                if (firstLine === reasoningTitle) {
                    lines.splice(firstNonEmptyIndex, 1);
                    text = lines.join("\n");
                }
            }
        }
        const options: MarkedOptions = { async: false };
        let html = marked.parse(text, options) as string;
        html = DOMPurify.sanitize(html);

        const searchStyle = 'display:flex;align-items:center;gap:5px;color:#65216e;padding:2px 0;font-size:0.8125rem';
        const iconStyle = 'font-size:1rem;font-variation-settings:\'FILL\' 0';

        const activeSearchHtml =
            `<div style="${searchStyle}">` +
            `<span class="material-symbols-outlined" style="${iconStyle};animation:pulse 2s cubic-bezier(0.4,0,0.6,1) infinite">travel_explore</span>` +
            '<span>Searching the web<span class="loading-dots"></span></span></div>';

        const staticSearchIcon =
            `<div style="${searchStyle}">` +
            `<span class="material-symbols-outlined" style="${iconStyle}">travel_explore</span>` +
            '<span>Searched the web</span></div>';

        // Replace DONE markers with a small static icon
        html = html.replace(/<p>\s*\[WEB_SEARCH_DONE[^\]]*\]\s*<\/p>/g, staticSearchIcon);
        html = html.replace(/\[WEB_SEARCH_DONE[^\]]*\]/g, staticSearchIcon);

        // Check if there's an unmatched START (still actively searching)
        const starts = (text.match(/\[WEB_SEARCH_START\]/g) || []).length;
        const dones = (text.match(/\[WEB_SEARCH_DONE[^\]]*\]/g) || []).length;
        const hasUnmatchedStart = isReasoningStreaming && starts > dones;

        if (hasUnmatchedStart) {
            // Show active indicator only on the last unmatched START
            let count = 0;
            html = html.replace(/<p>\s*\[WEB_SEARCH_START\]\s*<\/p>|\[WEB_SEARCH_START\]/g, () => {
                count++;
                return count === starts ? activeSearchHtml : "";
            });
        } else {
            // All done — remove all START markers
            html = html.replace(/<p>\s*\[WEB_SEARCH_START\]\s*<\/p>/g, "");
            html = html.replace(/\[WEB_SEARCH_START\]/g, "");
        }

        // File generation markers (code interpreter), mirroring the web search markers
        const activeFileGenHtml =
            `<div style="${searchStyle}">` +
            `<span class="material-symbols-outlined" style="${iconStyle};animation:pulse 2s cubic-bezier(0.4,0,0.6,1) infinite">terminal</span>` +
            '<span>Working on files<span class="loading-dots"></span></span></div>';

        const staticFileGenIcon =
            `<div style="${searchStyle}">` +
            `<span class="material-symbols-outlined" style="${iconStyle}">terminal</span>` +
            '<span>Created files</span></div>';

        html = html.replace(/<p>\s*\[FILE_GEN_DONE\]\s*<\/p>/g, staticFileGenIcon);
        html = html.replace(/\[FILE_GEN_DONE\]/g, staticFileGenIcon);

        const fileGenStarts = (text.match(/\[FILE_GEN_START\]/g) || []).length;
        const fileGenDones = (text.match(/\[FILE_GEN_DONE\]/g) || []).length;
        const hasUnmatchedFileGenStart = isReasoningStreaming && fileGenStarts > fileGenDones;

        if (hasUnmatchedFileGenStart) {
            let fileGenCount = 0;
            html = html.replace(/<p>\s*\[FILE_GEN_START\]\s*<\/p>|\[FILE_GEN_START\]/g, () => {
                fileGenCount++;
                return fileGenCount === fileGenStarts ? activeFileGenHtml : "";
            });
        } else {
            html = html.replace(/<p>\s*\[FILE_GEN_START\]\s*<\/p>/g, "");
            html = html.replace(/\[FILE_GEN_START\]/g, "");
        }

        return html;
    }, [activeReasoning, reasoningTitle, isReasoningStreaming]);

    let streamingDot: string =
        isStreaming && lastAnswer
            ? " <span class='material-symbols-outlined text-dot text-jtc-purple' style=\"font-variation-settings: 'FILL' 1;\"> circle</span>"
            : "";

    const parsedAnswer = useMemo(
        () => parseAnswerToHtml(messageContent, isStreaming, onCitationClicked, streamingDot),
        [answer]
    );
    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);
    const sanitizedThoughts = DOMPurify.sanitize(effectiveThoughts);

    const [copyIcon, setCopyIcon] = useState<string>("content_copy");
    const [exporting, setExporting] = useState<boolean>(false);
    const [iframeKey, setIframeKey] = useState(0);
    const [activeVideoTitle, setActiveVideoTitle] = useState<string>();

    function onThoughtsClicked() {
        onThoughtProcessClicked();
    }

    function onCopyClicked() {
        onCopyAnswerClicked();
        setCopyIcon("check");
        setTimeout(() => setCopyIcon("content_copy"), 2000);
    }

    const onAnswerShowVideo = async (citation: string, title: string) => {
        try {
            const videoInfo = parseVideoCitation(citation);
            if (!videoInfo) {
                console.error("Could not parse video citation:", citation);
                return;
            }
            const tokenData = await getVideoEmbedToken(videoInfo.videoId);
            const embedUrl = buildVideoEmbedUrl(
                tokenData.accountId,
                tokenData.videoId,
                tokenData.accessToken,
                tokenData.location,
                videoInfo.startTime
            );
            if (windowWidth < 1280) {
                window.open(embedUrl, "_blank");
            } else {
                onShowVideo(embedUrl);
                setActiveVideoTitle(title);
                setIframeKey(prevKey => prevKey + 1);
            }
        } catch (error) {
            console.error("Failed to get video embed URL:", error);
        }
    };

    const onAnswerCloseVideo = () => {
        onCloseVideo();
        setActiveVideoTitle(undefined);
    };

    const nextFrame = () => new Promise<void>(r => requestAnimationFrame(() => r()));

    function findCandidatesList(root: HTMLElement) {
        const lists = Array.from(root.querySelectorAll<HTMLOListElement | HTMLUListElement>("ol, ul"));
        const candidates = lists
            .map(l => ({ l, items: Array.from(l.querySelectorAll<HTMLLIElement>(":scope > li")) }))
            .filter(x => x.items.length >= 1 && x.items.length <= 5)
            .filter(x => x.items.filter(li => /\bDOB\b/i.test(li.textContent || "")).length >= Math.ceil(x.items.length / 2));
        if (candidates.length) {
            candidates.sort((a, b) => b.items.length - a.items.length);
            return candidates[0].l;
        }
        return null;
    }

    useEffect(() => {
        const root = chatStreamRef.current;
        if (!root) return;
        const listEl = findCandidatesList(root);
        if (!listEl) return;
        const items = Array.from(listEl.querySelectorAll<HTMLLIElement>(":scope > li"));
        items.forEach(li => {
            if (li.querySelector('input[type="checkbox"].wc-box')) return;
            const cb = document.createElement("input");
            cb.type = "checkbox";
            cb.className = "wc-box";
            cb.style.marginRight = "8px";
            cb.addEventListener("change", () => {
                if (!cb.checked) return;
                items.forEach(other => {
                    if (other !== li) {
                        const otherCb = other.querySelector<HTMLInputElement>('input[type="checkbox"].wc-box');
                        if (otherCb) otherCb.checked = false;
                    }
                });
            });
            li.insertBefore(cb, li.firstChild);
        });
    }, [sanitizedAnswerHtml]);

    async function onExportPdfClicked() {
        try {
            setExporting(true);
            await nextFrame();

            const answerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);
            let thoughtsHtml = thoughtsRef.current?.innerHTML ?? DOMPurify.sanitize(effectiveThoughts);
            thoughtsHtml = (thoughtsHtml || "<em>None</em>").replace(/&nbsp;/g, " ");
            thoughtsHtml = thoughtsHtml
                .replace(/(<li[^>]*>\s*<div[^>]*>\s*<strong>)\s*\d+\.\s*/g, "$1")
                .replace(/(<li[^>]*>\s*)\d+\.\s*(?=<)/g, "$1");
            thoughtsHtml = thoughtsHtml.replace(/thought\s*process/gi, "");

            let selectedHtml = "";
            try {
                const root = chatStreamRef.current;
                if (root) {
                    const listEl = findCandidatesList(root);
                    if (listEl) {
                        const checked = listEl.querySelector<HTMLInputElement>('input[type="checkbox"].wc-box:checked');
                        if (checked) {
                            const li = checked.closest("li");
                            if (li) {
                                const clone = li.cloneNode(true) as HTMLLIElement;
                                clone.querySelectorAll('input[type="checkbox"].wc-box').forEach(n => n.remove());
                                selectedHtml = DOMPurify.sanitize(clone.innerHTML);
                            }
                        }
                    }
                }
            } catch { }

            const selectedSection = selectedHtml
                ? `<div class="section"><h2>Selected match</h2><div class="wc-selected">${selectedHtml}</div></div><div class="hr"></div>`
                : "";

            const printHtml = `<!doctype html><html><head><meta charset="utf-8"><title>World-Check Watchlist Export</title><meta name="viewport" content="width=device-width, initial-scale=1"><style>@page { size: A4; margin: 0.5in; } html, body { height: 100%; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; font-family: "Segoe UI", Arial, sans-serif; color:#4b4b4b; } h1 { font-size:16pt; margin:0 0 8pt 0; color:#65216e; } h2 { font-size:13pt; margin:12pt 0 6pt 0; color:#65216e; } h3 { font-size:12pt; margin:10pt 0 6pt 0; color:#65216e; } .section { font-size:11pt; line-height:1.45; } .hr { height:1px; background:#ebebeb; margin:10pt 0; } .stream-table { width:100%; } .wc-selected { padding:10pt; border:1px solid #ebebeb; border-radius:8pt; } .page-break { break-before: page; } @media screen { body { margin: 24px; } .a4 { width: 1748px; } }</style></head><body><div class="a4"><h1>World-Check Watchlist Export</h1>${selectedSection}<div class="section"><h3>All Results</h3><div class="stream-table">${thoughtsHtml}</div></div></div><script>window.addEventListener('load', () => { setTimeout(() => { window.focus(); window.print(); }, 50); }); window.onafterprint = () => { setTimeout(() => window.close(), 300); };</script></body></html>`;

            const iframe = document.createElement("iframe");
            iframe.style.cssText = "position:fixed;width:0;height:0;opacity:0;pointer-events:none;border:0";
            document.body.appendChild(iframe);
            const onAfterPrint = () => { setTimeout(() => iframe.remove(), 0); window.removeEventListener("afterprint", onAfterPrint); };
            try {
                const doc = iframe.contentDocument || iframe.ownerDocument;
                if (!doc) throw new Error("Unable to access print document");
                doc.open(); doc.write(printHtml); doc.close();
                const win = iframe.contentWindow;
                if (!win) throw new Error("Unable to access print window");
                const cleanup = () => { setTimeout(() => iframe.remove(), 3000); };
                window.addEventListener("afterprint", onAfterPrint);
                win.addEventListener("afterprint", onAfterPrint);
                await new Promise(r => setTimeout(r, 75));
                win.focus(); win.print(); cleanup();
            } catch { iframe.remove(); throw new Error("Unable to initiate print"); }
        } catch (e) { console.error(e); } finally { setExporting(false); }
    }

    const getHostname = (url: string): string => {
        try {
            return new URL(url).hostname.replace(/^www\./, "");
        } catch {
            return url;
        }
    };

    const getFaviconUrl = (url: string): string => {
        const hostname = getHostname(url);
        return `https://www.google.com/s2/favicons?domain=${hostname}&sz=32`;
    };

    return (
        <div className="w-full">
            <div className={"transition-[padding] duration-200 py-[18px] px-4 text-base m-auto " + (appStateContext?.state.isChatHistoryOpen ? "" : "")}>
                <div className="mx-auto flex flex-1 text-base gap-4 lg:gap-6 lg:max-w-chat bg-jtc-white opacity-95 rounded-lg p-5">
                    <div className="flex-shrink-0 flex flex-col relative items-end">
                        <div>
                            <div className="pt-0">
                                <div className="outline outline-2 outline-jtc-purple flex h-8 w-8 items-center justify-center overflow-hidden rounded-full">
                                    <div className="relative p-1 pt-1.5 rounded-sm flex items-center justify-center h-8 w-8 text-[#000000]">
                                        <AnswerIcon />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="relative flex w-full min-w-0 flex-col">
                        <div className="flex-col gap-1 lg:gap-3">
                            <div className="flex flex-grow flex-col max-w-full">
                                <div className="flex flex-col gap-1 lg:gap-3">
                                    <div className="flex flex-grow flex-col max-w-full">
                                        <div className="min-h-[20px] text-message flex flex-col break-words w-full items-end overflow-x-auto gap-2">
                                            <div className="flex w-full flex-col gap-1 first:pt-[3px]">
                                                <div className="w-full break-words">

                                                    {/* Web search indicator — only shown when reasoning is NOT active */}
                                                    {isWebSearching && !activeReasoning && (
                                                        <div className="mb-3 flex items-center gap-2 text-sm text-[#7F7F7F]">
                                                            <span
                                                                className="material-symbols-outlined text-[1.125rem] text-jtc-purple animate-pulse"
                                                                style={{ fontVariationSettings: "'FILL' 0" }}
                                                            >
                                                                travel_explore
                                                            </span>
                                                            <span>
                                                                Searching the web
                                                                <span className="loading-dots" />
                                                            </span>
                                                        </div>
                                                    )}

                                                    {/* File generation indicator — reasoning markers cover the reasoning-active case */}
                                                    {((isGeneratingFiles && !activeReasoning) || isSavingFiles) && (
                                                        <div className="mb-3 flex items-center gap-2 text-sm text-[#7F7F7F]">
                                                            <span
                                                                className="material-symbols-outlined text-[1.125rem] text-jtc-purple animate-pulse"
                                                                style={{ fontVariationSettings: "'FILL' 0" }}
                                                            >
                                                                terminal
                                                            </span>
                                                            <span>
                                                                {isSavingFiles ? "Preparing files" : "Working on files"}
                                                                <span className="loading-dots" />
                                                            </span>
                                                        </div>
                                                    )}

                                                    {/* Reasoning section */}
                                                    {activeReasoning && (
                                                        <div className="mb-3">
                                                            <button
                                                                onClick={() => {
                                                                    if (isReasoningStreaming) {
                                                                        setReasoningExpanded(!reasoningExpanded);
                                                                    } else {
                                                                        setShowReasoning(!showReasoning);
                                                                    }
                                                                }}
                                                                className="flex items-center gap-1.5 text-sm text-[#7F7F7F] hover:text-jtc-purple transition-colors py-1"
                                                            >
                                                                <span
                                                                    className="material-symbols-outlined text-[1rem] transition-transform duration-200"
                                                                    style={{
                                                                        transform: (isReasoningStreaming ? reasoningExpanded : showReasoning) ? "rotate(90deg)" : "rotate(0deg)"
                                                                    }}
                                                                >
                                                                    chevron_right
                                                                </span>
                                                                {isReasoningStreaming ? (
                                                                    <span>{reasoningTitle || "Thinking"}<span className="loading-dots" /></span>
                                                                ) : (
                                                                    <span>{showReasoning ? "Hide thoughts" : (reasoningTitle || "Thought for a few seconds")}</span>
                                                                )}
                                                            </button>
                                                            <div
                                                                className="overflow-hidden transition-all duration-300 ease-in-out"
                                                                style={{
                                                                    maxHeight: (isReasoningStreaming ? reasoningExpanded : showReasoning) ? "300px" : "0px",
                                                                    opacity: (isReasoningStreaming ? reasoningExpanded : showReasoning) ? 1 : 0,
                                                                }}
                                                            >
                                                                <div
                                                                    ref={reasoningRef}
                                                                    className="mt-2 border-l-2 border-[#d9d9d9] pl-4 text-sm text-[#7F7F7F] max-h-[280px] overflow-y-auto leading-relaxed"
                                                                    dangerouslySetInnerHTML={{ __html: sanitizedReasoning }}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Answer content */}
                                                    <div
                                                        className="chat-stream stream-table text-base leading-[1.75]"
                                                        ref={chatStreamRef}
                                                        dangerouslySetInnerHTML={{ __html: sanitizedAnswerHtml }}
                                                    ></div>

                                                    {wasStopped && <p className="mt-1 text-sm italic text-[#7F7F7F]">You stopped this response</p>}
                                                    {droppedFiles.length > 0 && (
                                                        <p className="mt-1 text-sm italic text-[#7F7F7F]">
                                                            Older attachments were left out to fit the conversation limit: {droppedFiles.join(", ")}
                                                        </p>
                                                    )}

                                                    {/* Generated images rendered inline */}
                                                    {generatedImages.length > 0 && (
                                                        <div className="flex flex-col gap-2 my-2">
                                                            {generatedImages.map((file, idx) => (
                                                                <GeneratedImage key={idx} file={file} />
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Generated file download chips (non-image files only) */}
                                                    {generatedDownloads.length > 0 && <GeneratedFileChips files={generatedDownloads} />}

                                                    {/* Sources section */}
                                                    <div>
                                                        {(!!parsedAnswer.citations.length || (hasWebSearchSources && !(isStreaming && lastAnswer))) && (
                                                            <div className="font-semibold my-1">Sources</div>
                                                        )}

                                                        {/* File citations (HR assistant pattern) */}
                                                        {parsedAnswer.citations.map((x, i) => {
                                                            let sourceType = x.includes(".mp4") ? "mp4" : "pdf";
                                                            let title = x
                                                                .replace(/\.\w+#page=/, " - Page ")
                                                                .replace(/\.mp4\?url=.*?t=([\d.]+)/, " - $1 seconds");
                                                            let citationTitle = x.replace(/\.\w+#page=.*$/, "").replace(/\.mp4\?url=.*$/, "");
                                                            return (
                                                                <a
                                                                    key={i}
                                                                    href="#"
                                                                    className="flex flex-col text-jtc-purple hover:text-jtc-blue cursor-pointer"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        sourceType === "mp4"
                                                                            ? onAnswerShowVideo(x, citationTitle)
                                                                            : onCitationClicked(x, citationTitle);
                                                                    }}
                                                                >
                                                                    {`${i + 1}. ${title}`}
                                                                </a>
                                                            );
                                                        })}

                                                        {/* Web search source pills */}
                                                        {hasWebSearchSources && !(isStreaming && lastAnswer) && (
                                                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                                                                {webSearchSources.map((source, idx) => (
                                                                    <a
                                                                        key={idx}
                                                                        href={source.url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        title={source.title || source.url}
                                                                        className="inline-flex items-center gap-1.5 border border-[#e0e0e0] rounded-full pl-1.5 pr-2.5 py-1 text-xs text-[#4b4b4b] hover:border-jtc-purple hover:text-jtc-purple bg-white transition-colors cursor-pointer no-underline"
                                                                    >
                                                                        <img
                                                                            src={getFaviconUrl(source.url)}
                                                                            alt=""
                                                                            width={16}
                                                                            height={16}
                                                                            className="rounded-sm shrink-0"
                                                                            onError={(e) => {
                                                                                (e.target as HTMLImageElement).style.display = "none";
                                                                            }}
                                                                        />
                                                                        <span className="truncate max-w-[160px]">
                                                                            {getHostname(source.url)}
                                                                        </span>
                                                                    </a>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {activeVideo && (
                                                        <div className="relative w-full flex-col pt-4 hidden xl:flex">
                                                            <div className="flex justify-between">
                                                                <div className="flex grow justify-start max-w-[calc(100%-4rem)]">
                                                                    <div className="leading-7 font-semibold mt-0.5 mb-2.5 text-ellipsis overflow-x-hidden whitespace-nowrap">
                                                                        {activeVideoTitle}
                                                                    </div>
                                                                </div>
                                                                <div className="flex justify-end">
                                                                    <ButtonTooltip title="Hide source" placement="top">
                                                                        <button
                                                                            className="h-10 rounded-lg px-1 text-jtc-purple hover:text-jtc-blue cursor-pointer"
                                                                            onClick={onAnswerCloseVideo}
                                                                            aria-label="Hide source"
                                                                        >
                                                                            <span className="h-[24px] w-[24px] shrink-0 text-2xl material-symbols-outlined">
                                                                                visibility_off
                                                                            </span>
                                                                        </button>
                                                                    </ButtonTooltip>
                                                                </div>
                                                            </div>
                                                            <iframe
                                                                key={iframeKey}
                                                                title="Citation"
                                                                src={activeVideo}
                                                                width="100%"
                                                                height="400px"
                                                                allowFullScreen
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {(!isStreaming || !lastAnswer) && (
                                            <div className="mt-2 flex gap-3 ml-[-3px]">
                                                <div className="items-center justify-start rounded-xl p-1 flex">
                                                    <div className="flex items-center">
                                                        <span className="h-[30px] w-[30px]">
                                                            <ButtonTooltip title="Copy">
                                                                <button
                                                                    className="flex justify-center font-semibold text-jtc-purple hover:text-jtc-blue"
                                                                    onClick={() => onCopyClicked()}
                                                                >
                                                                    <span className="material-symbols-outlined text-[1.25rem]">{copyIcon}</span>
                                                                </button>
                                                            </ButtonTooltip>
                                                        </span>
                                                        {/* Show lightbulb when we have real thoughts OR web sources */}
                                                        {hasThoughts && (
                                                            <span className="h-[30px] w-[30px]">
                                                                <ButtonTooltip title={showThoughts ? "Hide thought process" : "Show thought process"}>
                                                                    <button
                                                                        className={
                                                                            "flex justify-center font-semibold text-jtc-purple pb-[3px] hover:text-jtc-blue " +
                                                                            (showThoughts ? "icon-underline" : "")
                                                                        }
                                                                        onClick={() => onThoughtsClicked()}
                                                                    >
                                                                        <span className="material-symbols-outlined text-[1.25rem]">lightbulb</span>
                                                                    </button>
                                                                </ButtonTooltip>
                                                            </span>
                                                        )}
                                                        {appStateContext?.state.assistant?.assistant === "wc" && hasThoughts && (
                                                            <span className="h-[30px] w-[30px]">
                                                                <ButtonTooltip title="Export as PDF">
                                                                    <button
                                                                        className="flex justify-center font-semibold text-jtc-purple hover:text-jtc-blue"
                                                                        onClick={onExportPdfClicked}
                                                                        disabled={exporting}
                                                                    >
                                                                        <span className="material-symbols-outlined text-[1.25rem]">
                                                                            {exporting ? "hourglass_top" : "file_save"}
                                                                        </span>
                                                                    </button>
                                                                </ButtonTooltip>
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Thought process section */}
                                        {showThoughts && (
                                            <div className="bg-jtc-grey py-4 px-5 rounded-lg mt-4">
                                                <div className="font-semibold text-xl mb-2">Thought process</div>
                                                <div
                                                    className="stream-table"
                                                    ref={thoughtsRef}
                                                    dangerouslySetInnerHTML={{ __html: sanitizedThoughts }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
